import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/app/lib/cn'

/**
 * Every button clears the 48px touch target from `--tap`, and none of them
 * depend on hover to be understood — the console has to work on a touch monitor
 * where hover does not exist.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'md' | 'lg' | 'xl'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Right-aligned hint such as ⏎ or Esc. Hidden on touch-only screens. */
  shortcut?: ReactNode
  block?: boolean
}

const sizes: Record<ButtonSize, string> = {
  md: 'min-h-[48px] px-5 text-[15px] rounded-[12px] gap-2',
  lg: 'min-h-[56px] px-7 text-[17px] rounded-[14px] gap-2.5',
  xl: 'min-h-[68px] px-9 text-[20px] rounded-[16px] gap-3',
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-on-brand font-semibold shadow-[0_1px_0_rgba(255,255,255,.18)_inset] ' +
    'active:bg-brand-hi active:scale-[.985]',
  secondary:
    'bg-raised text-ink font-medium ring-1 ring-line ' +
    'active:bg-line active:scale-[.985]',
  ghost: 'bg-transparent text-ink-muted font-medium active:bg-raised',
  danger: 'bg-transparent text-danger font-medium ring-1 ring-danger/40 active:bg-danger/10',
}

export function Button({
  variant = 'primary',
  size = 'md',
  shortcut,
  block,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap',
        'transition-[background-color,transform] duration-100',
        'disabled:pointer-events-none disabled:opacity-35',
        sizes[size],
        variants[variant],
        block && 'w-full',
        className,
      )}
    >
      {children}
      {shortcut && (
        <kbd
          className="ml-1 hidden rounded-[6px] bg-black/25 px-1.5 py-0.5 text-[11px] font-normal opacity-70 sm:inline-block"
          aria-hidden
        >
          {shortcut}
        </kbd>
      )}
    </button>
  )
}
